import React, { useState } from "react";
import { Plus, Settings2, Target, Trash2, CalendarClock } from "lucide-react";
import { cn } from "../../lib/utils";
import type {
  AssignmentEditorViewModel,
  CommerceMetadataEntryViewModel,
} from "../../contracts/commerce-admin";
import { CommerceObjectSelector } from "./CommerceObjectSelector";
import { WizardWorkspace, type WizardTab } from "../blueprints/WizardWorkspace";
import { DescriptionText, SectionHeading, Field, inputStyles, Surface, VerticalStack } from "../blocks";

function createEntryId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function PricebookAssignmentEditorWizard({ viewModel, className }: {
  viewModel?: AssignmentEditorViewModel | null;
  className?: string;
}) {
  const [activeTabId, setActiveTabId] = useState<string>("general");

  if (!viewModel || !viewModel.isOpen) {
    return null;
  }

  const {
    state,
    title,
    subtitle,
    saveActionLabel,
    subjectLabel = "Pricebook",
    value,
    onSave,
    onClose,
    onChange,
    onChangeTarget,
    pricebookOptions,
    publicationOptions,
    organizationOptions,
    tenantOptions,
    storeOptions,
    agentOptions,
    statusOptions,
    targetOptions,
    targetRules,
    validationErrors,
  } = viewModel;

  const isSaving = state === "saving";
  const isLoading = state === "loading";
  const { target } = value;

  const patchMetadataEntry = (
    index: number,
    patch: Partial<CommerceMetadataEntryViewModel>,
  ) => {
    onChange({
      metadata: value.metadata.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, ...patch } : entry,
      ),
    });
  };

  const addMetadataEntry = () => {
    onChange({
      metadata: [
        ...value.metadata,
        {
          id: createEntryId("assignment-metadata"),
          key: "",
          value: "",
          valueType: "string",
        },
      ],
    });
  };

  const removeMetadataEntry = (index: number) => {
    onChange({
      metadata: value.metadata.filter((_, entryIndex) => entryIndex !== index),
    });
  };

  const renderTargetInput = () => {
    switch (target.kind) {
      case "publication":
        return (
          <Field label="Publication" error={validationErrors["target.publicationId"]}>
            <CommerceObjectSelector
              options={publicationOptions}
              value={target.publicationId || ""}
              onChange={publicationId => onChangeTarget({ kind: "publication", publicationId })}
              placeholder="Choose publication"
              searchPlaceholder="Search publications..."
              emptyMessage="No publications are available for this catalog owner."
            />
          </Field>
        );
      case "organization":
        return (
          <Field label="Organization" error={validationErrors["target.organizationId"]}>
            <CommerceObjectSelector
              options={organizationOptions}
              value={target.organizationId || ""}
              onChange={organizationId => onChangeTarget({ kind: "organization", organizationId })}
              placeholder="Choose organization"
              searchPlaceholder="Search organizations..."
              emptyMessage="No organizations are available for assignment targeting yet."
            />
          </Field>
        );
      case "tenant":
        return (
          <Field label="Selling Tenant" error={validationErrors["target.sellingTenantId"]}>
            <CommerceObjectSelector
              options={tenantOptions}
              value={target.sellingTenantId || ""}
              onChange={sellingTenantId => onChangeTarget({ kind: "tenant", sellingTenantId })}
              placeholder="Choose selling tenant"
              searchPlaceholder="Search tenants..."
              emptyMessage="No selling tenants are available for assignment targeting yet."
            />
          </Field>
        );
      case "store":
        return (
          <Field label="Store" error={validationErrors["target.storeId"]}>
            <CommerceObjectSelector
              options={storeOptions}
              value={target.storeId || ""}
              onChange={storeId => onChangeTarget({ kind: "store", storeId })}
              placeholder="Choose store"
              searchPlaceholder="Search stores..."
              emptyMessage="No stores are available for assignment targeting yet."
            />
          </Field>
        );
      case "agent":
        return (
          <Field label="Agent" error={validationErrors["target.agentUserId"]}>
            <CommerceObjectSelector
              options={agentOptions}
              value={target.agentUserId || ""}
              onChange={agentUserId => onChangeTarget({ kind: "agent", agentUserId })}
              placeholder="Choose agent"
              searchPlaceholder="Search agents..."
              emptyMessage="No agents are available for assignment targeting yet."
            />
          </Field>
        );
      default:
        return <DescriptionText>Select a target strategy.</DescriptionText>;
    }
  };

  const tabs: WizardTab[] = [
    { id: "general", label: "General", icon: <Settings2 className="w-4 h-4" /> },
    { id: "target", label: "Target Strategy", icon: <Target className="w-4 h-4" /> },
    { id: "advanced", label: "Advanced", icon: <CalendarClock className="w-4 h-4" /> },
  ];

  const summaryItems = [
    { label: "Status", value: value.status.toUpperCase(), complete: true },
    { label: "Starts At", value: value.startsAt ? new Date(value.startsAt).toLocaleString() : "Immediate", complete: !!value.startsAt },
    { label: "Ends At", value: value.endsAt ? new Date(value.endsAt).toLocaleString() : "Never", complete: !!value.endsAt },
  ];

  return (
    <WizardWorkspace
      className={className}
      isOpen={viewModel.isOpen}
      isSaving={isSaving}
      title={title}
      subtitle={subtitle}
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      saveLabel={saveActionLabel}
      onSave={() => void onSave()}
      onClose={onClose}
      summaryItems={summaryItems}
    >
      <VerticalStack gap={8}>
        {isLoading ? (
          <Surface className="p-6">
            <DescriptionText>Loading assignment detail.</DescriptionText>
          </Surface>
        ) : (
          <>
            {activeTabId === "general" && (
              <VerticalStack gap={8}>
                <section>
                  <SectionHeading title="Identity & Window" subtitle="Define the core properties and active duration." />
                  <Surface className="mt-4 border-border/60 p-6">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <Field label={subjectLabel} error={validationErrors.pricebookId}>
                        <select
                          value={value.pricebookId}
                          onChange={event => onChange({ pricebookId: event.target.value })}
                          className={inputStyles}
                        >
                          <option value="">{`Choose ${subjectLabel.toLowerCase()}`}</option>
                          {pricebookOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Status">
                        <select
                          value={value.status}
                          onChange={event => onChange({ status: event.target.value })}
                          className={inputStyles}
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Starts At" description="Leave blank for immediate start">
                        <input
                          type="datetime-local"
                          value={value.startsAt || ""}
                          onChange={event => onChange({ startsAt: event.target.value })}
                          className={inputStyles}
                        />
                      </Field>
                      <Field label="Ends At" description="Leave blank for indefinite duration">
                        <input
                          type="datetime-local"
                          value={value.endsAt || ""}
                          onChange={event => onChange({ endsAt: event.target.value })}
                          className={inputStyles}
                        />
                      </Field>
                    </div>
                  </Surface>
                </section>
              </VerticalStack>
            )}

            {activeTabId === "target" && (
              <section>
                <SectionHeading title="Target Strategy" subtitle="Define what scope or entity this pricebook assignment applies to." />
                <Surface className="mt-4 border-border/60 p-6">
                  <VerticalStack gap={6}>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
                      {targetOptions.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => onChangeTarget({ kind: option.value as typeof target.kind })}
                          className={cn(
                            "rounded-md border px-3 py-2 text-left text-xs font-black transition-all",
                            target.kind === option.value
                              ? "border-primary/40 bg-primary/5 text-foreground"
                              : "border-border/40 bg-background text-muted-foreground hover:bg-muted/20 hover:text-foreground",
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>

                    <Surface className="border-border/60 bg-muted/10 p-5">
                      {renderTargetInput()}
                    </Surface>

                    {targetRules.length > 0 ? (
                      <VerticalStack gap={3}>
                        <h4 className="text-xs font-black tracking-widest uppercase text-muted-foreground">Target Scope Resolution</h4>
                        <VerticalStack gap={2}>
                          {targetRules.map(rule => (
                            <Surface key={rule.id} className="border-border/60 p-4">
                              <VerticalStack gap={1}>
                                <p className="text-sm font-black tracking-tight text-foreground">{rule.label}</p>
                                <p className="text-xs leading-relaxed text-muted-foreground">{rule.body}</p>
                              </VerticalStack>
                            </Surface>
                          ))}
                        </VerticalStack>
                      </VerticalStack>
                    ) : null}
                  </VerticalStack>
                </Surface>
              </section>
            )}

            {activeTabId === "advanced" && (
              <section>
                <SectionHeading title="Metadata" subtitle="Store any operational key-value annotations here." />
                <Surface className="mt-4 border-border/60 p-6">
                  <VerticalStack gap={4}>
                    {value.metadata.length === 0 ? (
                      <Surface className="rounded-xl border border-dashed border-border/60 bg-muted/10 py-6 text-center">
                        <DescriptionText>No metadata entries defined.</DescriptionText>
                      </Surface>
                    ) : (
                      <VerticalStack gap={3}>
                        {value.metadata.map((entry, index) => (
                          <Surface key={entry.id} className="border-border/60 p-3">
                            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.2fr_1.2fr_180px_auto]">
                              <input
                                value={entry.key}
                                disabled={entry.locked}
                                onChange={event => patchMetadataEntry(index, { key: event.target.value })}
                                className={cn(inputStyles, "font-mono")}
                                placeholder="metadata_key"
                              />
                              <input
                                value={entry.value}
                                disabled={entry.locked}
                                onChange={event => patchMetadataEntry(index, { value: event.target.value })}
                                className={cn(inputStyles, "font-mono")}
                                placeholder="value"
                              />
                              <select
                                value={entry.valueType}
                                disabled={entry.locked}
                                onChange={event =>
                                  patchMetadataEntry(index, {
                                    valueType: event.target.value as CommerceMetadataEntryViewModel["valueType"],
                                  })
                                }
                                className={inputStyles}
                              >
                                <option value="string">String</option>
                                <option value="number">Number</option>
                                <option value="boolean">Boolean</option>
                                <option value="json">JSON</option>
                              </select>
                              <button
                                type="button"
                                disabled={entry.locked}
                                onClick={() => removeMetadataEntry(index)}
                                className="inline-flex items-center justify-center rounded-md border border-border/40 px-3 py-2 text-xs font-bold text-muted-foreground transition-all hover:bg-red-50 hover:text-red-700 hover:border-red-200 disabled:opacity-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </Surface>
                        ))}
                      </VerticalStack>
                    )}

                    <button
                      type="button"
                      onClick={addMetadataEntry}
                      className="inline-flex items-center gap-2 self-start rounded-md border border-border/60 bg-muted/20 px-4 py-2 text-xs font-bold text-foreground shadow-sm transition-all hover:bg-muted/40 hover:border-border/80"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Metadata Entry
                    </button>
                  </VerticalStack>
                </Surface>
              </section>
            )}
          </>
        )}
      </VerticalStack>
    </WizardWorkspace>
  );
}
